/**
 * Call history list (issue 26)
 *
 * Columns: datetime, organization, QR, caller, attended-by staff,
 * connection status, duration. Filters: date range, organization, staff,
 * call status, free text. CSV export of the filtered set (client-side,
 * see docs/api-notes.md for why).
 *
 * The API only exposes isCallConnected (not callstatus) on rows, so the
 * status column shows attended / not attended; the status filter still
 * works server-side via filterKey=callstatus.
 */

import { useContext, useMemo, useState, useCallback } from 'react';
import {
  List,
  TextField,
  FunctionField,
  useDataProvider,
  useGetList,
  useListContext,
  useNotify,
  useTranslate,
} from 'react-admin';
import { Box, Button, Chip, useTheme } from '@mui/material';
import {
  FilterList as FilterListIcon,
  FileDownloadOutlined as ExportIcon,
} from '@mui/icons-material';
import { CustomDatagrid } from '@/components/common';
import { compactListSx } from '@/components/common/compactListSx';
import { actionButtonSx, filterToggleSx } from '@/components/common/ListPageHeader';
import {
  CollapsibleFilterPanel,
  FilterExpandContext,
} from '@/components/common/CollapsibleFilterPanel';
import {
  CustomTextInputFilter,
  CustomSelectInputFilter,
  CustomDateInputFilter,
} from '@/components/common/CustomFilters';
import { escapeCsvCell } from '@/utils/exportCsv';

/** callstatus values written by the backend (incl. queue states of v1.2.0.0) */
const CALL_STATUS_KEYS = [
  'CALL_ENDED',
  'ANSWER_CALL',
  'MISSED_CALL',
  'NO_ANSWER',
  'CALL_REJECTED',
  'REJTD',
  'QUEUED',
  'QUEUE_TIMEOUT',
  'STAFF_UNAVAILABLE',
  'CONNECTION_ERROR',
];

/** Render the stored datetime as-is (no browser timezone shift) */
const formatStoredDateTime = (value: unknown): string => {
  const iso = String(value ?? '');
  if (iso.length < 16) return '';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} ${iso.slice(11, 16)}`;
};

const callerWithPhone = (record: any): string => {
  const name = record?.caller || record?.user_info?.name || '';
  const phone = record?.user_info?.phone;
  return phone ? `${name} (${phone})` : name;
};

const CallHistoryActions = ({
  orgNames,
  qrNames,
}: {
  orgNames: Record<number, string>;
  qrNames: Record<number, string>;
}) => {
  const filterExpand = useContext(FilterExpandContext);
  const theme = useTheme();
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { filterValues, sort } = useListContext();
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const { data } = await dataProvider.getList('callDetails', {
        filter: filterValues,
        sort: sort ?? { field: 'start_time', order: 'DESC' },
        pagination: { page: 1, perPage: 10000 },
      });
      if (!data?.length) {
        notify('resources.callDetails.export.empty', { type: 'info' });
        return;
      }
      const headers = [
        translate('resources.callDetails.fields.start_time'),
        translate('resources.callDetails.fields.end_time'),
        translate('resources.callDetails.fields.duration'),
        translate('resources.callDetails.fields.user_info'),
        translate('resources.callDetails.fields.organization_id'),
        translate('resources.callDetails.fields.qrcode_id'),
        translate('resources.callDetails.fields.user_name'),
        translate('resources.callDetails.fields.callstatus'),
        translate('resources.callDetails.fields.booking_id'),
      ];
      if (data.length >= 1000) {
        notify('resources.callDetails.export.capped', { type: 'warning' });
      }
      const lines = data.map((row: any) =>
        [
          formatStoredDateTime(row.start_time),
          formatStoredDateTime(row.end_time),
          row.duration ?? '',
          callerWithPhone(row),
          orgNames[row.organization_id] ?? row.organization_id ?? '',
          qrNames[row.qrcode_id] ?? row.qrcode_id ?? '',
          row.user_name ?? '',
          translate(
            `resources.callDetails.connection.${row.isCallConnected ? 'yes' : 'no'}`
          ),
          row.booking_id ?? '',
        ]
          .map((value) => `"${escapeCsvCell(String(value)).replace(/"/g, '""')}"`)
          .join(';')
      );
      // BOM so Excel opens the file as UTF-8
      const csv = '﻿' + [headers.join(';'), ...lines].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `historial-llamadas-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notify('ra.notification.http_error', { type: 'error' });
    } finally {
      setExporting(false);
    }
  }, [dataProvider, filterValues, sort, notify, translate, orgNames, qrNames]);

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Button
        onClick={() => filterExpand?.setExpanded(!filterExpand.expanded)}
        variant={filterExpand?.expanded ? 'contained' : 'outlined'}
        sx={filterToggleSx(theme, !!filterExpand?.expanded)}
        startIcon={<FilterListIcon />}
      >
        {translate('common.filters.label', { _: 'Filtros' })}
      </Button>
      <Button
        variant="contained"
        sx={actionButtonSx}
        startIcon={<ExportIcon />}
        onClick={handleExport}
        disabled={exporting}
      >
        {translate('ra.action.export')}
      </Button>
    </Box>
  );
};

export const CallHistoryList = () => {
  const translate = useTranslate();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filterExpandValue = useMemo(
    () => ({ expanded: filtersExpanded, setExpanded: setFiltersExpanded }),
    [filtersExpanded]
  );

  // Organization and staff choices for the filters; also reused to render
  // names in the table without per-row reference lookups.
  const { data: organizations } = useGetList('organizations', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'full_name', order: 'ASC' },
  });
  const { data: staff } = useGetList('staff', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'full_name', order: 'ASC' },
  });
  const { data: qrcodes } = useGetList('qrcodes', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'qr_name', order: 'ASC' },
  });

  const orgNames = useMemo(() => {
    const map: Record<number, string> = {};
    (organizations ?? []).forEach((org: any) => {
      map[org.user_id] = org.company_name || org.full_name;
    });
    return map;
  }, [organizations]);

  const qrNames = useMemo(() => {
    const map: Record<number, string> = {};
    (qrcodes ?? []).forEach((qr: any) => {
      map[qr.qrcode_id] = qr.qr_name;
    });
    return map;
  }, [qrcodes]);

  const filters = useMemo(
    () => [
      <CollapsibleFilterPanel
        key="filters"
        source="_cfp"
        alwaysOn
        alwaysVisibleCount={0}
        defaultExpanded
      >
        <CustomTextInputFilter
          source="q"
          label={translate('resources.callDetails.filters.q')}
          alwaysOn
        />
        <CustomDateInputFilter
          source="date_from"
          label={translate('resources.callDetails.filters.date_from')}
        />
        <CustomDateInputFilter
          source="date_to"
          label={translate('resources.callDetails.filters.date_to')}
        />
        <CustomSelectInputFilter
          source="organization_id"
          label={translate('resources.callDetails.filters.organization')}
          choices={(organizations ?? []).map((org: any) => ({
            id: org.user_id,
            name: org.company_name || org.full_name,
          }))}
        />
        <CustomSelectInputFilter
          source="user_id"
          label={translate('resources.callDetails.filters.staff')}
          choices={(staff ?? []).map((member: any) => ({
            id: member.user_id,
            name: member.full_name,
          }))}
        />
        <CustomSelectInputFilter
          source="callstatus"
          label={translate('resources.callDetails.filters.status')}
          choices={CALL_STATUS_KEYS.map((key) => ({
            id: key,
            name: translate(`resources.callDetails.status.${key}`, { _: key }),
          }))}
        />
      </CollapsibleFilterPanel>,
    ],
    [translate, organizations, staff]
  );

  return (
    <FilterExpandContext.Provider value={filterExpandValue}>
      <List
        sx={compactListSx}
        filters={filters}
        actions={<CallHistoryActions orgNames={orgNames} qrNames={qrNames} />}
        sort={{ field: 'start_time', order: 'DESC' }}
        exporter={false}
        title={translate('resources.callDetails.name', { smart_count: 2 })}
      >
        <CustomDatagrid primarySource="caller" secondarySource="user_name" bulkActionButtons={false}>
          <FunctionField
            source="start_time"
            render={(record: any) => formatStoredDateTime(record?.start_time)}
          />
          <FunctionField
            source="organization_id"
            render={(record: any) =>
              orgNames[record?.organization_id] ?? record?.organization_id ?? ''
            }
            sortable={false}
          />
          <FunctionField
            source="qrcode_id"
            render={(record: any) => qrNames[record?.qrcode_id] ?? record?.qrcode_id ?? ''}
            sortable={false}
          />
          <FunctionField source="caller" render={callerWithPhone} />
          <TextField source="user_name" />
          <FunctionField
            source="isCallConnected"
            render={(record: any) => (
              <Chip
                size="small"
                color={record?.isCallConnected ? 'success' : 'default'}
                label={
                  record?.isCallConnected
                    ? translate('resources.callDetails.connection.yes')
                    : translate('resources.callDetails.connection.no')
                }
              />
            )}
          />
          <TextField source="duration" sortable={false} />
        </CustomDatagrid>
      </List>
    </FilterExpandContext.Provider>
  );
};

export default CallHistoryList;
